import React from 'react';
import PropTypes from 'prop-types';
import {Utilities} from '@learningblocks/exp-constants';
import {Question} from '../Components/Question';
import {QuestionWrapper} from '../Components/QuestionWrapper';
import {MainTitle} from '../Components/MainTitle';
import {Footer} from '../Components/Footer';
import {TotalAttempts} from '../Components/TotalAttempts';
import {SubmitButton} from '../Components/SubmitButton';
import {Options} from './Options';
import './style.scss';

const {QUESTION_CONTENTS} = Utilities;

/**
 * Experimental-Design Question component [Step-Select]:
 *  - You will have n number of steps.
 *  - You will have a dropdown with n number of options
 *  - You will have to select the option corresponding to step.
 */
export function StepSelect(props) {
  const hasMoreAttempts = () =>
    props.totalAttempts > 0 || props.totalAttempts === -10;

  const isStepUntouched = (step) =>
    step.showFeedback === false && step.droppedId === null;

  const isStepCorrect = (step) => step.value === step.droppedId;

  const onSubmit = () => props.submitAnswer(props.id, props.type);

  const checkIfUnsubmittedOptions = () => {
    const hasUnsubmittedOption = props.answerSteps
      .filter((step) => !isStepDisabled(step))
      .some((step) => {
        if (isStepUntouched(step)) return false;
        else if (isStepCorrect(step) && step.showFeedback) return false;
        else return true;
      });
    return hasUnsubmittedOption;
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else return !checkIfUnsubmittedOptions();
  };

  const isStepDisabled = (step) => {
    if (isStepCorrect(step)) {
      if (step.showFeedback) return true;
      else return false;
    } else {
      if (hasMoreAttempts()) return false;
      else return true;
    }
  };

  const getStepFeedback = (step) => {
    if (step.showFeedback) {
      if (isStepCorrect(step)) return step.feedbackSuccess;
      else return step.feedbackError;
    } else {
      return '';
    }
  };

  const selectOption = (params) =>
    props.onDropdownSelect({...params, componentID: props.id});

  const renderDropDowns = (key) => {
    return props.answerSteps.map((step, id) => (
      <Options
        key={`${key}_${id}`}
        disabled={isStepDisabled(step)}
        feedback={getStepFeedback(step)}
        label={step.step}
        options={props.options}
        placeholder={props.optionPlaceholder}
        selectedOptionID={step.droppedId}
        selectOption={selectOption}
        showFeedback={step.showFeedback}
        stepID={step.value}
      />
    ));
  };

  const renderQuestion = (key) => (
    <Question
      key={key}
      text={props.question}
      className='stepSelect__question'
    />
  );

  const renderByType = (type, key) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key);
      case QUESTION_CONTENTS.ANSWER_STEPS:
        return renderDropDowns(key);
      default:
        return null;
    }
  };

  const renderContents = () => props.content.map(renderByType);

  const wrapperClass = ` stepSelect ${props.className}`;
  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle title={props.title} className='stepSelect__mainTitle' />
      {renderContents()}
      <Footer className='stepSelect__footer'>
        <TotalAttempts
          totalAttempts={props.totalAttempts}
          className='stepSelect__footer__totalAttempts'
        />
        <SubmitButton disabled={setSubmitDisabled()} onSubmit={onSubmit} />
      </Footer>
    </QuestionWrapper>
  );
}

StepSelect.propTypes = {
  /**
   * List specifying the proper order of the steps.
   * Also holds the label for step. Correct/Wrong feedback
   * messages to displayed when at the event.
   */
  answerSteps: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Label for the step
       */
      step: PropTypes.string,
      /**
       * Id for correct step
       */
      value: PropTypes.number,
      /**
       * Message to be displayed when correct option is submitted
       */
      feedbackSuccess: PropTypes.string,
      /**
       * Message to be displayed when incorrect option is submitted
       */
      feedbackError: PropTypes.string,
      /**
       * The Id of dropped option
       */
      droppedId: PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
      ]),
      /**
       * Boolean value specifying if current option should be displayed.
       */
      showFeedback: PropTypes.bool,
    })
  ).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.oneOfType([
    PropTypes.oneOf(['question']),
    PropTypes.string,
  ]).isRequired,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The placeholder string to be displayed for all step dropdowns
   */
  optionPlaceholder: PropTypes.string.isRequired,
  /**
   * The list of steps that need to be arranged.
   * Passed in order of how they should be displayed in dropdowns.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The unique value for the option
       */
      value: PropTypes.number,
      /**
       * Text to be displayed for the option
       */
      label: PropTypes.string,
    })
  ).isRequired,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,
  /**
   * callback funciton to be executed when an option
   * is selected from the dropdown
   */
  onDropdownSelect: PropTypes.func.isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(['DDQ']),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * Title which used to display as main title
   */
  title: PropTypes.string,
};

StepSelect.defaultProps = {
  answerSteps: [],
  className: 'question',
  type: 'DDQ',
  complete: false,
  content: [],
  optionPlaceholder: '',
  options: [],
  question: '',
  totalAttempts: 0,
};
